// src/api/services/applicationService.ts
import { AnimalType } from "../../types";
import api from "../axios";

/** ------- APPLICATION LIST & DETAIL ------- */

export interface ApplicationSummary {
  uuid: string;
  createdAt: string;
  moveInDate: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  status: "created" | "in-progress" | "completed";
  unit: {
    id: number;
    name: string;
    property: {
      id: number;
      name: string;
    };
  };
}

export function getApplications() {
  return api.get<ApplicationSummary[]>("/applications");
}

export interface ApplicationItemDto<T = any> {
  type: string;
  status: "created" | "in-progress" | "completed";
  data: T;
}

export interface ApplicationDetailsDto {
  uuid: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  status: string;
  payment: "none" | "tenant" | "pm";
  paymentStatus: "none" | "pending" | "paid";
  unit: {
    id: number;
    name: string;
    property: { id: number; name: string };
  };
  items: ApplicationItemDto<any>[];
}

export function getApplicationDetails(uuid: string) {
  return api.get<ApplicationDetailsDto>(`/applications/${uuid}/details`);
}

/** ------- APPLICATION INFO ------- */

export interface ApplicationInfoDto {
  ssn: string;
  rentersInsurance: boolean;
  rentersInsuranceInterested: boolean;
  animal: AnimalType;
  usResident?: boolean;
  bestDescription?: string;
  nonUsBestDescription?: string;
  annualIncome?: number;
  monthlyRent?: number;
}

export function getApplicationInfo(uuid: string) {
  return api.get<ApplicationInfoDto>(`/applications/${uuid}/info`);
}

export function putApplicationInfo(uuid: string, data: ApplicationInfoDto) {
  return api.put(`/applications/${uuid}/info`, data);
}

/** ------- APPLICATION PAYMENT ------- */

export interface ProcessPaymentDto {
  paymentMethodId: string;
  amount: number; // in cents, e.g., 2100 for $21.00
}

export interface PaymentIntentDto {
  clientSecret: string;
  paymentIntentId: string;
}

export function createPaymentIntent(uuid: string, data: ProcessPaymentDto) {
  return api.post<PaymentIntentDto>(`/applications/${uuid}/payment/intent`, data);
}

export function confirmPayment(uuid: string, paymentIntentId: string) {
  return api.post(`/applications/${uuid}/payment/confirm`, { paymentIntentId });
}